// type reference
// https://github.rbx.com/Roblox/snd-domain/tree/master/services/domain-documents/src/DomainDocuments.CreatorResources/Models/Elasticsearch/Documentation
import {
  classesBasePath,
  datatypesBasePath,
  enumsBasePath,
  globalsBasePath,
  librariesBasePath,
} from '@modules/apiReference/page/constants/apiReferenceConstants';
import {
  DocumentationContentType as DocsSearchDocumentationContentType,
  DocumentationSubType as DocsSearchDocumentationSubType,
  DocumentationThirdType as DocsSearchDocumentationThirdType,
} from '@rbx/creator-docs-search';

/**
 * Using const objects instead of enums because TypeScript doesn't allow mixing
 * string literals (like All = '') with computed values from imported packages
 * (like LuaAPI = DocsSearchDocumentationContentType.LuaAPI) in the same enum.
 *
 * Const objects with 'as const' provide the same type safety as enums while
 * allowing us to use the actual values from the external @rbx/creator-docs-search package.
 * This ensures compatibility with the validate() function which expects Record<string, any>.
 */
export const DocumentationContentType = {
  All: '',
  LuaAPI: DocsSearchDocumentationContentType.LuaAPI,
  Article: DocsSearchDocumentationContentType.Article,
  CloudAPI: DocsSearchDocumentationContentType.CloudAPI,
  Video: DocsSearchDocumentationContentType.Video,
  DevForum: DocsSearchDocumentationContentType.DevForum,
} as const;

export type DocumentationContentType =
  (typeof DocumentationContentType)[keyof typeof DocumentationContentType];

export const DocumentationArticleSubType = {
  Guide: DocsSearchDocumentationSubType.Guide,
  Education: DocsSearchDocumentationSubType.Education,
} as const;

export type DocumentationArticleSubType =
  (typeof DocumentationArticleSubType)[keyof typeof DocumentationArticleSubType];

export const DocumentationLuaType = {
  Class: DocsSearchDocumentationSubType.Class,
  DataType: DocsSearchDocumentationSubType.DataType,
  Enum: DocsSearchDocumentationSubType.Enum,
  Global: DocsSearchDocumentationSubType.Global,
  Library: DocsSearchDocumentationSubType.Library,
} as const;

export type DocumentationLuaType = (typeof DocumentationLuaType)[keyof typeof DocumentationLuaType];

export const luaTypeToPathMapping: Record<DocumentationLuaType, string> = {
  [DocumentationLuaType.Class]: classesBasePath,
  [DocumentationLuaType.DataType]: datatypesBasePath,
  [DocumentationLuaType.Enum]: enumsBasePath,
  [DocumentationLuaType.Global]: globalsBasePath,
  [DocumentationLuaType.Library]: librariesBasePath,
};

export const DocumentationCloudApiType = {
  CloudApi: DocsSearchDocumentationSubType.CloudAPI,
  Legacy: DocsSearchDocumentationSubType.Legacy,
  V1: DocsSearchDocumentationSubType.V1,
  V2: DocsSearchDocumentationSubType.V2,
  Features: DocsSearchDocumentationSubType.Features,
  Instruction: 'Instruction',
} as const;

export type DocumentationCloudApiType =
  (typeof DocumentationCloudApiType)[keyof typeof DocumentationCloudApiType];

export const DocumentationDevForumSubType = {
  Announcements: DocsSearchDocumentationSubType.Announcements,
  RobloxStaff: DocsSearchDocumentationSubType.RobloxStaff,
  CommunityResources: DocsSearchDocumentationSubType.CommunityResources,
  CommunityTutorials: DocsSearchDocumentationSubType.CommunityTutorials,
} as const;

export type DocumentationDevForumSubType =
  (typeof DocumentationDevForumSubType)[keyof typeof DocumentationDevForumSubType];

const DocumentationSubType = {
  ...DocumentationArticleSubType,
  ...DocumentationLuaType,
  ...DocumentationCloudApiType,
};

export type DocumentationSubType =
  | DocumentationArticleSubType
  | DocumentationLuaType
  | DocumentationCloudApiType
  | DocumentationDevForumSubType; // Allow for future extensions or custom types

export const DocumentationCloudApiSubType = {
  Endpoint: DocsSearchDocumentationThirdType.Endpoint,
  API: DocsSearchDocumentationThirdType.API,
  Resource: DocsSearchDocumentationThirdType.Resource,
} as const;

export type DocumentationCloudApiSubType =
  (typeof DocumentationCloudApiSubType)[keyof typeof DocumentationCloudApiSubType];

export const DocumentationLuaSubType = {
  Property: DocsSearchDocumentationThirdType.Property,
  Event: DocsSearchDocumentationThirdType.Event,
  Method: DocsSearchDocumentationThirdType.Method,
  Function: DocsSearchDocumentationThirdType.Function,
  Callback: DocsSearchDocumentationThirdType.Callback,
  Constant: DocsSearchDocumentationThirdType.Constant,
  Constructor: DocsSearchDocumentationThirdType.Constructor,
  MathOperation: DocsSearchDocumentationThirdType.MathOperation,
  EnumItem: DocsSearchDocumentationThirdType.EnumItem,
  CodeSample: DocsSearchDocumentationThirdType.CodeSample,
} as const;

export type DocumentationLuaSubType =
  (typeof DocumentationLuaSubType)[keyof typeof DocumentationLuaSubType];

// https://stackoverflow.com/questions/48478361/how-to-merge-two-enums-in-typescript
export const DocumentationThirdType = {
  ...DocumentationCloudApiSubType,
  ...DocumentationLuaSubType,
};
export type DocumentationThirdType = DocumentationCloudApiSubType | DocumentationLuaSubType;
